import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  CompanyEquipment,
  CompanyEquipmentType,
  EQUIPMENT_TYPE_LABELS,
} from "../types/companyEquipment";
import * as Sentry from "@sentry/react";

interface YallyCompany {
  id: number;
  name: string;
  archived?: boolean;
}

interface CompanyEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingItem?: CompanyEquipment | null;
}

export const CompanyEquipmentModal: React.FC<CompanyEquipmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingItem,
}) => {
  const { session } = useSession();
  const isEditing = !!editingItem;

  // Companies list
  const [companies, setCompanies] = useState<YallyCompany[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Form state
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [equipmentType, setEquipmentType] = useState<string>("");
  const [serialNumber, setSerialNumber] = useState("");
  const [inUseBy, setInUseBy] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Company search
  const [companySearch, setCompanySearch] = useState("");

  // Pre-populate form when editing
  useEffect(() => {
    if (editingItem) {
      setSelectedCompanyId(String(editingItem.companyId));
      setEquipmentType(editingItem.companyEquipmentType || "");
      setSerialNumber(editingItem.serialNumber || "");
      setInUseBy(editingItem.inUseBy || "");
    }
  }, [editingItem]);

  // Fetch companies when modal opens
  useEffect(() => {
    if (!isOpen || !session) return;

    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/companies/all`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setCompanies(data);
        }
      } catch (error) {
        console.error("Failed to load companies:", error);
        toast.error("Fout bij laden van vennootschappen");
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [isOpen, session]);

  // Filter active companies and apply search
  const filteredCompanies = useMemo(() => {
    const active = companies.filter((c) => !c.archived);
    if (!companySearch.trim()) return active;
    const search = companySearch.toLowerCase();
    return active.filter((c) => c.name.toLowerCase().includes(search));
  }, [companies, companySearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompanyId) {
      toast.error("Vennootschap is verplicht");
      return;
    }

    if (!serialNumber.trim()) {
      toast.error("Serienummer is verplicht");
      return;
    }

    setIsSubmitting(true);

    try {
      const body: Record<string, unknown> = {
        companyId: selectedCompanyId,
        serialNumber: serialNumber.trim(),
        companyEquipmentType: equipmentType || null,
        inUseBy: inUseBy.trim() || null,
      };

      const url = isEditing
        ? `${API_URL}/company-equipment/${editingItem!.id}`
        : `${API_URL}/company-equipment`;

      const response = await fetchWithAuth(
        url,
        {
          method: isEditing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        session,
      );

      if (!response.ok) {
        throw new Error(
          `Fout bij ${isEditing ? "bijwerken" : "aanmaken"}: ${response.status}`,
        );
      }

      toast.success(
        isEditing
          ? "Bedrijfsuitrusting succesvol bijgewerkt!"
          : "Bedrijfsuitrusting succesvol aangemaakt!",
      );
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error("Error saving company equipment:", error);
      Sentry.captureException(error, {
        tags: {
          component: "CompanyEquipmentModal",
          action: isEditing ? "update" : "create",
        },
        extra: { serialNumber, equipmentType },
      });
      toast.error(
        `Fout bij ${isEditing ? "bijwerken" : "aanmaken"}: ${error.message || "Onbekende fout"}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedCompanyId("");
    setEquipmentType("");
    setSerialNumber("");
    setInUseBy("");
    setCompanySearch("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? "Bedrijfsuitrusting bewerken"
              : "Nieuwe bedrijfsuitrusting toevoegen"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company */}
          <div className="space-y-2">
            <Label>
              Vennootschap <span className="text-red-600">*</span>
            </Label>
            {isLoadingCompanies ? (
              <p className="text-neutral-secondary-mid text-sm">
                Vennootschappen laden...
              </p>
            ) : (
              <Select
                value={selectedCompanyId}
                onValueChange={setSelectedCompanyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer vennootschap" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <Input
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      placeholder="Zoek vennootschap..."
                      className="h-8"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredCompanies.map((company) => (
                    <SelectItem
                      key={company.id}
                      value={String(company.id)}
                    >
                      {company.name}
                    </SelectItem>
                  ))}
                  {filteredCompanies.length === 0 && (
                    <p className="text-neutral-secondary-mid px-2 py-1.5 text-sm">
                      Geen vennootschappen gevonden
                    </p>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Equipment Type */}
          <div className="space-y-2">
            <Label htmlFor="equipmentType">Type</Label>
            <Select value={equipmentType} onValueChange={setEquipmentType}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(CompanyEquipmentType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {EQUIPMENT_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Serial Number */}
          <div className="space-y-2">
            <Label htmlFor="serialNumber">
              Serienummer <span className="text-red-600">*</span>
            </Label>
            <Input
              id="serialNumber"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Bijv. ABC-12345"
              required
            />
          </div>

          {/* In Use By */}
          <div className="space-y-2">
            <Label htmlFor="inUseBy">In gebruik door</Label>
            <Input
              id="inUseBy"
              value={inUseBy}
              onChange={(e) => setInUseBy(e.target.value)}
              placeholder="Naam invoeren"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEditing
                  ? "Bezig met opslaan..."
                  : "Bezig met aanmaken..."
                : isEditing
                  ? "Opslaan"
                  : "Toevoegen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
